import { ClockCircleOutlined, DownOutlined, QuestionCircleOutlined, UpOutlined } from '@ant-design/icons';
import { List } from '@refinedev/antd';
import { useTranslate } from '@refinedev/core';
import { Button, ConfigProvider, Tooltip, Typography } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';

/**
 * E57 · D-nav-fixes-2026-10-19 (founder Q-nav-6 A) — a tool's list on the client page is ONE toolbar line: title · count ·
 * "?" · Create. Standalone (no `fixedClientId`) it is the Refine `<List>` it always was, prop for prop.
 *
 * Embedded (`fixedClientId` set): no page card, no breadcrumb, no Show names (the page shows one client, whose name is in
 * the header — the list passes its Show names only when standalone), the Create button compact (32 px); the "?" holds the
 * list's intro when it has one; column titles at most two lines, the full title in a tooltip (`embeddedTableProps`).
 */
type ListProps = React.ComponentProps<typeof List>;

const useStyles = createStyles(({ token, css }) => ({
  toolbar: css`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
    margin: 4px 0 8px;
  `,
  title: css`
    &.ant-typography {
      margin: 0 !important;
      font-size: 18px;
      line-height: 1.25;
      white-space: nowrap;
    }
  `,
  count: css`
    color: ${token.colorTextTertiary};
    font-size: 13.5px;
    white-space: nowrap;
  `,
  help: css`
    color: ${token.colorTextSecondary};
    cursor: help;
  `,
  actions: css`
    margin-left: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
  `,
  body: css`
    .ant-table-thead > tr > th {
      padding: 10px 12px;
      vertical-align: middle;
    }
    .ant-table-tbody > tr > td {
      padding: 10px 12px;
    }
    .cc-th-clamp,
    .cc-th-clamp .ant-table-column-title {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .cc-th-clamp:has(.ant-table-column-sorters) {
      display: block;
    }
  `,
}));

/** The plain text of a header's children (for its tooltip). */
const textOf = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (React.isValidElement(node)) return textOf((node.props as { children?: React.ReactNode }).children);
  return '';
};

/** A header cell whose title is clamped to two lines, the full title in the native tooltip (Q-nav-6 A). */
const ClampedHeaderCell: React.FC<React.ThHTMLAttributes<HTMLTableCellElement>> = ({ children, title, ...rest }) => {
  const text = textOf(children).trim();
  return (
    <th {...rest} title={title ?? (text || undefined)}>
      <div className="cc-th-clamp">{children}</div>
    </th>
  );
};

const CLAMPED_HEADER = { header: { cell: ClampedHeaderCell } };

/** Spread onto a list's main `<Table>`: on the client page its column titles take at most two lines. */
export const embeddedTableProps = (fixedClientId?: string | null): { components?: typeof CLAMPED_HEADER } =>
  fixedClientId ? { components: CLAMPED_HEADER } : {};

const totalOf = (tableProps: unknown): number | null => {
  const tp = tableProps as { loading?: unknown; pagination?: unknown } | undefined;
  if (!tp || tp.loading === true) return null;
  const p = tp.pagination as { total?: unknown } | false | undefined;
  return p && typeof p.total === 'number' ? p.total : null;
};

export const ToolList: React.FC<
  ListProps & {
    fixedClientId?: string | null;
    /** The list's table props — the count beside the title is its total (embedded only). */
    tableProps?: unknown;
    /** The list's intro, behind the "?" (embedded only). */
    help?: React.ReactNode;
    testId?: string;
  }
> = ({ fixedClientId, tableProps, help, testId, children, ...listProps }) => {
  const t = useTranslate();
  const { styles } = useStyles();
  if (!fixedClientId) return <List {...listProps}>{children}</List>;

  const { title, headerButtons, headerProps } = listProps;
  const buttons =
    typeof headerButtons === 'function'
      ? (headerButtons as (ctx: { defaultButtons: React.ReactNode }) => React.ReactNode)({ defaultButtons: null })
      : headerButtons ?? (headerProps as { extra?: React.ReactNode } | undefined)?.extra ?? null;
  const count = totalOf(tableProps);
  return (
    <div data-testid={testId ?? 'cc-tool-list'} className="cc-tool-list">
      <div className={styles.toolbar} data-testid="cc-tool-list-toolbar">
        <Typography.Title level={3} className={styles.title} data-testid="cc-tool-list-title">
          {title as React.ReactNode}
        </Typography.Title>
        {count !== null ? (
          <span className={styles.count} data-testid="cc-tool-list-count">
            {count}
          </span>
        ) : null}
        {help ? (
          <Tooltip title={help}>
            <QuestionCircleOutlined
              className={styles.help}
              aria-label={t('clientPage.tools.about')}
              tabIndex={0}
              data-testid="cc-tool-list-help"
            />
          </Tooltip>
        ) : null}
        <ConfigProvider theme={{ components: { Button: { controlHeight: 32, paddingInline: 14, contentFontSize: 14 } } }}>
          <div className={styles.actions}>{buttons}</div>
        </ConfigProvider>
      </div>
      <div className={styles.body}>{children}</div>
    </div>
  );
};

const useHelperStyles = createStyles(({ token, css }) => ({
  line: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    font-size: 13.5px;
    background: #fdebc8;
    color: #4a2f00;
    border-radius: 12px;
    padding: 6px 12px;
    margin: 0 0 8px;
  `,
  grow: css`
    flex: 1;
  `,
  list: css`
    background: ${token.colorBgContainer};
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: 12px;
    margin: -4px 0 10px;
    padding: 6px 12px;
  `,
}));

/**
 * E57 · D-nav-fixes-2026-10-19 (Q-nav-6 A) — a helper card above a list (Unfinished thought tests, Unfinished designs):
 * NOT drawn when it holds nothing (the caller returns null then); otherwise ONE quiet line — "<title>: <n>", what the
 * first one needs — and Show opens the list under it.
 */
export const HelperLine: React.FC<{
  title: React.ReactNode;
  count: number;
  summary?: React.ReactNode;
  testId: string;
  children: React.ReactNode;
}> = ({ title, count, summary, testId, children }) => {
  const t = useTranslate();
  const { styles } = useHelperStyles();
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testId} data-count={count}>
      <div className={styles.line} data-testid={`${testId}-line`}>
        <ClockCircleOutlined />
        <b>
          {title}: <span data-testid={`${testId}-count`}>{count}</span>
        </b>
        {summary ? <span>{summary}</span> : null}
        <span className={styles.grow} />
        <Button
          size="small"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          data-testid={`${testId}-toggle`}
        >
          {open ? t('clientPage.tools.hideList') : t('clientPage.tools.showList')} {open ? <UpOutlined /> : <DownOutlined />}
        </Button>
      </div>
      {open ? (
        <div className={styles.list} data-testid={`${testId}-list`}>
          {children}
        </div>
      ) : null}
    </div>
  );
};
